import clsx from "clsx";
import React from "react";
import { useNavigate } from "react-router-dom";

import { getTimeFromDateString } from "../../../libs/dateFormat";

import { HistoryItem } from "../../../services/historyServices";
import {
  REPORT_STATUS,
  getStepFromReportStatusCode,
  getStringFromReportStatusCode,
} from "../../../services/mangerReportServices";

import VerticalRule from "../../Rule/Vertical";
import { ReportStatusRadioIcon } from "../../../pages/Manager/Report";
import HorizontalRule from "../../Rule/Horizontal";
import ProgressBar from "../../ProgressBar";

type Props = { index: number; item: HistoryItem; items: HistoryItem[] };

export default function HistoryIncommodityItem({ index, item, items }: Props) {
  const navigate = useNavigate();

  return (
    <React.Fragment key={index}>
      <div className="flex gap-2.5 relative">
        <div className="h-full w-[24px] flex justify-center absolute z-0">
          {items.length > 1 && (
            <VerticalRule
              className={clsx([
                "!h-full !bg-[#d9d9d9]",
                {
                  "!h-[calc(100%-24px)] ": index === 0,
                  "!h-[24px]": index === items.length - 1,
                },
              ])}
              wrapperClassName={clsx([
                {
                  "!items-end": index === 0,
                  "!items-start": index === items.length - 1,
                },
              ])}
            />
          )}
        </div>
        <div className="w-[24px] pt-[18px] z-10">
          <ReportStatusRadioIcon
            status={getStringFromReportStatusCode[Number(item.status)]}
          />
        </div>
        <div className="flex flex-col gap-1 pt-[18px]">
          <div className="text-base font-bold">
            {getTimeFromDateString(item.createdAt)}
          </div>
          <div className=" text-xs text-[#9f9f9f]">
            {item.createdAt.split(" ")[0]}
          </div>
          {item.status === REPORT_STATUS.CANCELED.toString() && (
            <CancelCompleteBadge />
          )}
        </div>
        <div
          className={clsx([
            "p-[15px] border border-brand-neutral-10 rounded-[10px] flex flex-col gap-2.5 flex-1 h-fit",
            {
              "bg-brand-neutral-05":
                item.status === REPORT_STATUS.CANCELED.toString(),
            },
          ])}
          onClick={() => navigate(`/history/${item.type}/${item.serviceId}`)}
        >
          <div className="flex gap-2.5">
            <HistoryReportIcon />
            <div className="flex flex-col gap-0.5">
              <div
                className={clsx([
                  {
                    " text-brand-neutral-50":
                      item.status === REPORT_STATUS.CANCELED.toString(),
                  },
                ])}
              >
                {item.lowerCategoryName}
              </div>
              <div className="text-xs text-brand-neutral-50">
                {item.midCategoryName} · {item.description}
              </div>
            </div>
          </div>
          {item.status !== REPORT_STATUS.CANCELED.toString() && (
            <>
              <HorizontalRule margin="0" />
              <div className=" bg-brand-primary-magenta bg-clip-text text-xs text-transparent font-semibold w-fit">
                {item?.statusName}
              </div>
              <ProgressBar
                currentStep={getStepFromReportStatusCode[Number(item.status)]}
                wrapperClassName="!p-0"
                className="h-2.5 !bg-brand-primary-magenta"
                rounded
                nonSticky
              />
            </>
          )}
        </div>
      </div>
      {index < items.length - 1 && (
        <div className="h-5 w-[24px] flex justify-center">
          <VerticalRule className="!h-full !bg-[#d9d9d9]" />
        </div>
      )}
    </React.Fragment>
  );
}

export const CancelCompleteBadge = () => (
  <div className="rounded px-1.5 py-1 text-xs text-brand-neutral-80 bg-[#eee] w-fit">
    취소 완료
  </div>
);

export const HistoryReportIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32 28.7603L32 8.58398L8 8.58398L8 28.7603L16.5945 28.7603L20 32.584L23.4077 28.7603L32 28.7603Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M24.9715 18.6533C24.9715 21.0325 22.7455 22.9613 19.9996 22.9613C17.2536 22.9613 15.0276 21.0325 15.0276 18.6533"
      stroke="url(#paint0_linear_1398_39309)"
      strokeWidth="1.5"
    />
    <path
      d="M24.9606 14.7168L24.973 14.7168"
      stroke="url(#paint1_linear_1398_39309)"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M15.0278 14.7168L15.0402 14.7168"
      stroke="url(#paint2_linear_1398_39309)"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1398_39309"
        x1="15.0276"
        y1="21.5922"
        x2="22.6675"
        y2="19.7435"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="0.992254" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1398_39309"
        x1="24.9606"
        y1="15.399"
        x2="24.9707"
        y2="15.399"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="0.992254" stopColor="#E100A3" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_1398_39309"
        x1="15.0278"
        y1="15.399"
        x2="15.0379"
        y2="15.399"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EA27C2" />
        <stop offset="0.992254" stopColor="#E100A3" />
      </linearGradient>
    </defs>
  </svg>
);
